(ns practitest.frontend.components.project-assistant
  (:require
    [clojure.string :as str]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common-entities.metadata :as md]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.fetch :refer [fetch-json pt-fetch]]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.grid.server :as server]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.markdown3 :refer [markdown-preview]]
    [practitest.frontend.components.navigation :as navigation]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.components.project-assistant.actions :as actions]
    [practitest.frontend.components.project-assistant.tools :as tools]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.feedback :as feedback]
    [practitest.frontend.components2.search-box :as search-box]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.components2.tabs :refer [tab tabs-container]]
    [practitest.frontend.device-connection.stream :as stream]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(def max-inline-possible-values 20)

(defn- build-fields-for-assistant [db]
  (->> (subs/entity-fields-grouped (:fields db) (:entity-fields db))
       (mapcat (fn [[entity-kw fields]]
                 (map (fn [f]
                        (cond-> {:name (:name f)
                                 :field_format (or (:type f) "text")
                                 :entities [(name entity-kw)]}
                          (and (contains? #{"list" "multilist" "linkedlist"} (:type f))
                               (seq (:values f))
                               (<= (count (:values f)) max-inline-possible-values))
                          (assoc :possible_values (:values f))))
                      fields)))
       (group-by (juxt :name :field_format))
       (mapv (fn [[_ fs]]
               (-> (first fs)
                   (assoc :entities (vec (mapcat :entities fs))))))))

(defn- active-convo-storage-key [user-id project-id]
  (str "assistant:active:" user-id ":" project-id))

(defn- read-active-convo-id [user-id project-id]
  (when (and user-id project-id)
    (some-> (.-localStorage js/window)
            (.getItem (active-convo-storage-key user-id project-id))
            (js/parseInt)
            (as-> n (when-not (js/isNaN n) n)))))

;; --- subscriptions ---

;; TODO: newer re-frame has some useful syntactic sugar for this kind of subscriptions
;;       (re-frame/reg-sub ::project-assistant :-> :project-assistant)
;;       (re-frame/reg-sub ::panel-open? :<- ::project-assistant :-> :panel-open?)

(re-frame/reg-sub
  ::project-assistant
  (fn [db _]
    (:project-assistant db)))

(re-frame/reg-sub
  ::panel-open?
  :<- [::project-assistant]
  (fn [state _]
    (:panel-open? state)))

(re-frame/reg-sub
  ::conversation-title
  :<- [::project-assistant]
  (fn [state _]
    (get-in state [:conversation :title])))

(re-frame/reg-sub
  ::active-tab
  :<- [::project-assistant]
  (fn [state _]
    (or (:active-tab state) :chat)))

(re-frame/reg-sub
  ::conversations
  :<- [::project-assistant]
  (fn [state _]
    (->> (:conversations state)
         (sort-by :last_message_at #(compare %2 %1)))))

(re-frame/reg-sub
  ::history-search
  :<- [::project-assistant]
  (fn [state _]
    (:history-search state "")))

(re-frame/reg-sub
  ::filtered-conversations
  :<- [::conversations]
  :<- [::history-search]
  (fn [[conversations query] _]
    (let [query (str/lower-case query)]
      (filter #(str/includes? (str/lower-case (or (:title %) "")) query) conversations))))

(re-frame/reg-sub
  ::rename-state
  :<- [::project-assistant]
  (fn [state _]
    (:rename-state state)))

(re-frame/reg-sub
  ::messages
  :<- [::project-assistant]
  (fn [state _]
    (:messages state)))

(re-frame/reg-sub
  ::streaming?
  :<- [::project-assistant]
  (fn [state _]
    (:streaming? state)))

(re-frame/reg-sub
  ::connected?
  :<- [::project-assistant]
  (fn [state _]
    (:connected? state)))

(re-frame/reg-sub
  ::error
  :<- [::project-assistant]
  (fn [state _]
    (:error state)))

(re-frame/reg-sub
  ::budget-warning?
  :<- [::project-assistant]
  (fn [state _]
    (= "soft_limit" (get-in state [:budget :status]))))

;; --- state (outside of app-db) ---

(defonce conn-state (atom nil))

;; --- effects ---

(re-frame/reg-fx
  ::connect!
  (fn [{:keys [url token]}]
    ;; XXX: connect! expects fully constructed URL, while send-message! expects base URL, refactor it
    (let [conn (stream/connect!
                 {:url (str url "/stream/1/connect")
                  :headers {"Authorization" (str "Bearer " token)}
                  :on-open #(re-frame/dispatch [::connected])
                  :on-message (fn [msg]
                                (case (:type msg)
                                  ;; NOTE: "start" is ignored now,
                                  ;;       ::stream-token creates assistant message on first token, no need to setup
                                  "start" nil
                                  "token" (re-frame/dispatch [::stream-token (:content msg)])
                                  "tool-calls" (re-frame/dispatch [::execute-tool-calls (:calls msg)])
                                  "usage" (re-frame/dispatch [::report-usage msg])
                                  "done" (re-frame/dispatch [::stream-done])
                                  "error" (re-frame/dispatch [::stream-error (:message msg)])
                                  "title" (re-frame/dispatch [::title-received msg])
                                  nil))
                  :on-close (fn [_]
                              (re-frame/dispatch [::disconnected]))
                  :on-error (fn [err]
                              (re-frame/dispatch [::disconnected])
                              (re-frame/dispatch [::connection-error (str err)]))})]
      (reset! conn-state conn))))

(re-frame/reg-fx
  ::send-init!
  (fn [{:keys [url conn-id fields tools]}]
    (stream/send-message! url conn-id
                          {:type "init"
                           :fields fields
                           :tools tools})))

(re-frame/reg-fx
  ::send!
  (fn [{:keys [url conn-id message]}]
    (stream/send-message! url conn-id message)))

(re-frame/reg-fx
  ::disconnect!
  (fn [_]
    (when-let [conn @conn-state]
      (stream/disconnect! conn)
      (reset! conn-state nil))))

(defn- fetch-service-token [project-id]
  (-> (fetch-json (broutes/get-url "service_tokens#create" {:p_id project-id})
                  {:method "POST"})
      (.then (fn [{:keys [body]}]
               (:token body)))))

(re-frame/reg-fx
  ::fetch-token!
  (fn [{:keys [project-id]}]
    (-> (fetch-service-token project-id)
        (.then (fn [token]
                 (re-frame/dispatch [::token-received token])))
        (.catch (fn [err]
                  (re-frame/dispatch [::connection-error (str err)]))))))

(defn- execute-get-field-values [fields-meta users field-name]
  (let [found (some (fn [[_ v]]
                      (when (or (= (:label v) field-name)
                                (= (name (:name v)) field-name))
                        v))
                    fields-meta)]
    (js/Promise.resolve
      (js/JSON.stringify
        (clj->js
          (cond
            (nil? found)
            {:error (str "Unknown field: " field-name)}

            (= :user (:type found))
            {:field_name (or (:label found) (name (:name found)))
             :values (->> users
                          (filter #(= "user" (:type %)))
                          (keep :email)
                          (vec))
             :note "Values are user emails. CURRENT_USER is also valid for filtering by the current session user."}

            (seq (:possible-values found))
            {:field_name (or (:label found) (name (:name found)))
             :values (:possible-values found)}

            :else
            {:field_name (or (:label found) (name (:name found)))
             :values []
             :note "This field has no enumerable values."}))))))

(defn- execute-ptql-query [project-id entity-module query]
  (let [entity (md/entity-by-module entity-module)
        route-name (get-in md/entities-metadata [entity :urls :fetch-list])]
    (when-not route-name (throw (js/Error. (str "Unknown entity type: " entity-module))))
    (-> (pt-fetch (broutes/get-url route-name {:p_id project-id} {"ptql" query "page" "1" "per_page" "10"})
                  {:method "GET"})
        (.then #(.text %)))))

(defn- execute-find-matching-views [project-id entity-type query]
  (-> (pt-fetch (broutes/get-url "custom_views#find_matching" {:p_id project-id})
                {:method "POST" :body {:entity_type entity-type :query query}})
      (.then #(.text %))))

(defn- send-tool-results! [url conn-id results]
  (stream/send-message! url conn-id {:type "tool-results" :results results}))

(re-frame/reg-fx
  ::execute-tools!
  (fn [{:keys [url calls conn-id project-id fields-meta users extra-results]}]
    (let [promises (into-array
                     (map (fn [{:keys [id name args]}]
                            (-> (case name
                                  "get_field_values" (execute-get-field-values fields-meta
                                                                               users
                                                                               (:field_name args))
                                  "ptql_query" (execute-ptql-query project-id
                                                                   (:entity_type args)
                                                                   (:query args))
                                  "find_matching_views" (execute-find-matching-views project-id
                                                                                     (:entity_type args)
                                                                                     (:query args))
                                  (js/Promise.resolve (str "Unknown tool: " name)))
                                (.then (fn [result]
                                         {:tool-call-id id :result result}))
                                (.catch (fn [err]
                                          {:tool-call-id id :result (str "Error: " err)}))))
                          calls))]
      (-> (js/Promise.all promises)
          (.then (fn [results]
                   (send-tool-results! url conn-id (into (vec results) (or extra-results [])))))
          (.catch (fn [err]
                    (re-frame/dispatch [::stream-error (str "Tool execution failed: " err)])))))))

(re-frame/reg-fx
  ::send-tool-results!
  (fn [{:keys [url conn-id results]}]
    (send-tool-results! url conn-id results)))

(re-frame/reg-fx
  ::post-usage!
  (fn [{:keys [project-id prompt-tokens completion-tokens model byok]}]
    (-> (pt-fetch (broutes/get-url "assistant#usage" {:p_id project-id})
                  {:method "POST"
                   :body {:prompt_tokens prompt-tokens
                          :completion_tokens completion-tokens
                          :model model
                          :byok byok}})
        (.then #(.text %)) ;; drain the (empty) body, otherwise we get errors in chrome
        (.catch #(js/console.warn "Failed to report usage:" %)))))

(re-frame/reg-fx
  ::check-budget!
  (fn [{:keys [project-id text]}]
    (-> (fetch-json (broutes/get-url "assistant#budget" {:p_id project-id}) {})
        (.then (fn [{:keys [body]}]
                 (re-frame/dispatch [::budget-checked body text])))
        (.catch (fn [_err]
                  (re-frame/dispatch [::budget-checked {:allowed true :status "ok"} text]))))))

(re-frame/reg-fx
  ::create-conversation!
  (fn [{:keys [project-id model on-success]}]
    (-> (fetch-json (broutes/get-url "assistant_conversations#create" {:p_id project-id})
                    {:method "POST" :body {:model model}})
        (.then (fn [{:keys [body]}]
                 (re-frame/dispatch (conj on-success body)))))))

(re-frame/reg-fx
  ::persist-messages!
  (fn [{:keys [project-id conversation-id messages on-success]}]
    (-> (fetch-json (broutes/get-url "assistant_messages#create"
                                     {:p_id project-id
                                      :assistant_conversation_id conversation-id})
                    {:method "POST" :body {:messages messages}})
        (.then (fn [{:keys [status ok? body]}]
                 (cond
                   (= 409 status) (re-frame/dispatch [::position-conflict])
                   ok? (re-frame/dispatch (conj on-success body))
                   :else (js/console.warn "Failed to persist messages:" status))))
        (.catch #(js/console.warn "Persistence error:" %)))))

(re-frame/reg-fx
  ::patch-message!
  (fn [{:keys [project-id conversation-id message-id data]}]
    (-> (fetch-json (broutes/get-url "assistant_messages#update"
                                     {:p_id project-id
                                      :assistant_conversation_id conversation-id
                                      :id message-id})
                    {:method "PATCH" :body {:data data}})
        (.catch #(js/console.warn "Failed to patch message:" %)))))

(re-frame/reg-fx
  ::set-active-conversation!
  (fn [{:keys [user-id project-id convo-id]}]
    (when (and user-id project-id)
      (let [storage (.-localStorage js/window)
            key (active-convo-storage-key user-id project-id)]
        (if convo-id
          (.setItem storage key (str convo-id))
          (.removeItem storage key))))))

(re-frame/reg-fx
  ::load-conversation!
  (fn [{:keys [project-id conversation-id on-success on-not-found]}]
    (-> (fetch-json (broutes/get-url "assistant_conversations#show" {:p_id project-id :id conversation-id}) {})
        (.then (fn [{:keys [status ok? body]}]
                 (cond
                   ok? (re-frame/dispatch (conj on-success body))
                   (= 404 status) (when on-not-found (re-frame/dispatch on-not-found))
                   :else (js/console.warn "Failed to load conversation:" status))))
        (.catch #(js/console.warn "Load conversation error:" %)))))

(re-frame/reg-fx
  ::patch-conversation-title!
  (fn [{:keys [project-id conversation-id title]}]
    (-> (pt-fetch (broutes/get-url "assistant_conversations#update" {:p_id project-id :id conversation-id})
                  {:method "PATCH" :body {:title title}})
        (.then #(.text %))
        (.catch #(js/console.warn "Failed to persist auto title:" %)))))

(re-frame/reg-fx
  ::fetch-conversations!
  (fn [{:keys [project-id on-success]}]
    (-> (fetch-json (broutes/get-url "assistant_conversations#index" {:p_id project-id}) {})
        (.then (fn [{:keys [body]}]
                 (re-frame/dispatch (conj on-success body))))
        (.catch #(js/console.warn "Failed to fetch conversations:" %)))))

(re-frame/reg-fx
  ::delete-conversation!
  (fn [{:keys [project-id conversation-id on-success]}]
    (-> (pt-fetch (broutes/get-url "assistant_conversations#destroy" {:p_id project-id :id conversation-id})
                  {:method "DELETE"})
        (.then #(.text %))
        (.then (fn [_]
                 (re-frame/dispatch (conj on-success conversation-id))))
        (.catch #(js/console.warn "Failed to delete conversation:" %)))))

(re-frame/reg-fx
  ::restore-conversation!
  (fn [{:keys [project-id conversation-id on-success]}]
    (-> (fetch-json (broutes/get-url "assistant_conversations#restore" {:p_id project-id :id conversation-id})
                    {:method "POST"})
        (.then (fn [{:keys [body]}]
                 (re-frame/dispatch (conj on-success body))))
        (.catch #(js/console.warn "Failed to restore conversation:" %)))))

;; --- events ---

(defn- flush-pending-actions
  "Moves buffered pending-action messages into the main :messages list.
  Called from `::stream-done` / `::stream-error` so confirmation cards always appear AFTER the assistant's response."
  [db]
  (let [buffer (get-in db [:project-assistant :pending-actions-buffer])]
    (if (seq buffer)
      (-> db
          (update-in [:project-assistant :messages] (fnil into []) buffer)
          (update-in [:project-assistant] dissoc :pending-actions-buffer))
      db)))

(defn- message->server
  "Converts a frontend message map to the server shape:
  top-level keys stay, `:content`/etc go into `:data` (snake_cased)."
  [{:keys [role position id content context-entity tool-call-id tool-name args status result] :as _msg}]
  {:position position
   :role role
   :data (cond-> {}
           content (assoc :content content)
           context-entity (assoc :context_entity context-entity)
           tool-call-id (assoc :tool_call_id tool-call-id)
           tool-name (assoc :tool_name tool-name)
           args (assoc :args args)
           status (assoc :status (name status))
           result (assoc :result result))})

(defn- server->message
  "Converts a server message map (snake_case data) to the frontend shape."
  [{:keys [id position role data] :or {data {}}}]
  (cond-> {:role role :position position :id id}
    (:content data) (assoc :content (:content data))
    (:context_entity data) (assoc :context-entity (:context_entity data))
    (:tool_call_id data) (assoc :tool-call-id (:tool_call_id data))
    (:tool_name data) (assoc :tool-name (:tool_name data))
    (:args data) (assoc :args (:args data))
    (:status data) (assoc :status (keyword (:status data)))
    (:result data) (assoc :result (:result data))))

(re-frame/reg-event-fx
  ::toggle-panel
  (fn [{db :db} _]
    (let [opening? (not (get-in db [:project-assistant :panel-open?]))
          connected? (get-in db [:project-assistant :connected?])
          project-id (subs/get-current-project-id db)
          user-id (:id (subs/get-current-user db))
          has-convo? (some? (get-in db [:project-assistant :conversation]))
          active-id (when (and opening? (not has-convo?))
                      (read-active-convo-id user-id project-id))]
      (when opening? (mixpanel/track "Assistant Opened"))
      (cond-> {:db (update-in db [:project-assistant :panel-open?] not)}
        (and opening? (not connected?))
        (assoc ::fetch-token! {:project-id project-id})

        active-id
        (assoc :dispatch [::load-conversation active-id])))))

(re-frame/reg-event-db
  ::close-panel
  (fn [db _]
    (assoc-in db [:project-assistant :panel-open?] false)))

(re-frame/reg-event-fx
  ::token-received
  (fn [{db :db} [_ token]]
    {:db (assoc-in db [:project-assistant :token] token)
     ::connect! {:url (get-in db [:options :project-assistant-url]) :token token}}))

(re-frame/reg-event-fx
  ::connected
  (fn [{db :db} _]
    {:db (-> db
             (assoc-in [:project-assistant :connected?] true)
             (assoc-in [:project-assistant :error] nil))
     ::send-init! {:url (get-in db [:options :project-assistant-url])
                   :conn-id (stream/conn-id @conn-state)
                   :fields (build-fields-for-assistant db)
                   :tools tools/catalog}}))

(re-frame/reg-event-fx
  ::disconnected
  (fn [{db :db} _]
    (let [panel-open? (get-in db [:project-assistant :panel-open?])
          project-id (subs/get-current-project-id db)]
      (reset! conn-state nil)
      (cond-> {:db (-> db
                       (assoc-in [:project-assistant :connected?] false)
                       (assoc-in [:project-assistant :streaming?] false))}
        panel-open?
        (assoc ::fetch-token! {:project-id project-id})))))

(re-frame/reg-event-fx
  ::connection-error
  (fn [{db :db} [_ error-msg]]
    {:db (-> db
             (assoc-in [:project-assistant :connected?] false)
             (assoc-in [:project-assistant :error] error-msg))}))

(re-frame/reg-event-fx
  ::send-message
  (fn [{db :db} [_ text]]
    (let [project-id (subs/get-current-project-id db)]
      {:db (assoc-in db [:project-assistant :streaming?] true)
       ::check-budget! {:project-id project-id :text text}})))

(re-frame/reg-event-db
  ::stream-token
  (fn [db [_ content]]
    (let [messages (get-in db [:project-assistant :messages])
          last-msg (last messages)]
      (if (= "assistant" (:role last-msg))
        (update-in db [:project-assistant :messages (dec (count messages)) :content] str content)
        (update-in db [:project-assistant :messages] (fnil conj [])
                   {:role "assistant" :content content})))))

(defn- pending-action-msg [{:keys [id name args]}]
  {:role "pending-action"
   :tool-call-id id
   :tool-name name
   :args args
   :status :pending
   :result nil})

(re-frame/reg-event-fx
  ::execute-tool-calls
  (fn [{db :db} [_ calls]]
    (doseq [{:keys [name]} calls]
      (mixpanel/track "Assistant Tool Used" {"tool" name}))
    (let [read? (fn [{:keys [name]}]
                  (= :read (get-in tools/metadata [name :category] :read)))
          read-calls (filter read? calls)
          pending-calls (remove read? calls)
          ;; informational messages for read actions
          db (reduce (fn [db {:keys [name args]}]
                       (update-in db [:project-assistant :messages] (fnil conj [])
                                  {:role "tool-call"
                                   :content (case name
                                              "get_field_values"
                                              (str "Looking up values for: `" (:field_name args) "`")

                                              "ptql_query"
                                              (let [entity (md/entity-by-module (:entity_type args))
                                                    plural (get-in md/entities-metadata [entity :plural-name :default])]
                                                (str "Searching " plural ": `" (:query args) "`"))

                                              "find_matching_views"
                                              (str "Finding matching views for: `" (:query args) "`")

                                              (str "Running " name))}))
                     db read-calls)
          ;; pending-action messages are buffered; flushed on stream-done so they appear AFTER the assistant's response
          db (reduce (fn [db call]
                       (update-in db [:project-assistant :pending-actions-buffer] (fnil conj [])
                                  (pending-action-msg call)))
                     db pending-calls)
          pending-results (mapv (fn [{:keys [id]}]
                                  {:tool-call-id id
                                   :result "Confirmation card presented to user"})
                                pending-calls)
          assistant-url (get-in db [:options :project-assistant-url])
          conn-id (stream/conn-id @conn-state)]
      (if (seq read-calls)
        {:db db
         ::execute-tools! {:url assistant-url
                           :calls read-calls
                           :conn-id conn-id
                           :project-id (subs/get-current-project-id db)
                           :fields-meta (:fields db)
                           :users (:users db)
                           :extra-results pending-results}}
        {:db db
         ::send-tool-results! {:url assistant-url
                               :conn-id conn-id
                               :results pending-results}}))))

;; NOTE: this is for debugging "action framework" in REPL
(re-frame/reg-event-db
  ::dev-add-pending-action
  (fn [db [_ tool-name args]]
    (update-in db [:project-assistant :messages] (fnil conj [])
               (pending-action-msg {:id (str "dev-" (.getTime (js/Date.)))
                                    :name tool-name
                                    :args args}))))

(re-frame/reg-event-fx
  ::report-usage
  (fn [{db :db} [_ {:keys [prompt_tokens completion_tokens model byok]}]]
    (let [project-id (subs/get-current-project-id db)]
      {::post-usage! {:project-id project-id
                      :prompt-tokens prompt_tokens
                      :completion-tokens completion_tokens
                      :model model
                      :byok byok}})))

(re-frame/reg-event-fx
  ::budget-checked
  (fn [{db :db} [_ {:keys [allowed status used_cents budget_cents]} text]]
    (let [db (assoc-in db [:project-assistant :budget]
                       {:status status :used_cents used_cents :budget_cents budget_cents})]
      (if allowed
        (let [current-entity (:current-entity db)
              module (when current-entity
                       (get-in md/entities-metadata [current-entity :module]))
              position (get-in db [:project-assistant :next-position] 0)
              user-msg {:role "user" :content text :context-entity module :position position}
              display-messages (-> (get-in db [:project-assistant :messages] [])
                                   (conj user-msg))
              with-context (fn [{:keys [role content context-entity] :as m}]
                             (if (and (= role "user") context-entity)
                               (assoc m :content (str "[Context: currently viewing " context-entity "]\n" content))
                               m))
              llm-messages (->> display-messages
                                (filter #(contains? #{"user" "assistant"} (:role %)))
                                (remove #(str/blank? (:content %)))
                                (map with-context)
                                (vec))
              conn-id (stream/conn-id @conn-state)
              project-id (subs/get-current-project-id db)
              convo-id (get-in db [:project-assistant :conversation :id])
              model (get-in db [:project-assistant :conversation :model] "gpt5.4")]
          (mixpanel/track "Assistant Message Sent")
          (let [base-db (-> db
                            (assoc-in [:project-assistant :messages] display-messages)
                            (assoc-in [:project-assistant :streaming?] true)
                            (assoc-in [:project-assistant :error] nil)
                            (update-in [:project-assistant :next-position] (fnil inc 0)))
                url (get-in db [:options :project-assistant-url])]
            (if convo-id
              {:db base-db
               ::send! {:url url
                        :conn-id conn-id
                        :message {:type "chat"
                                  :messages llm-messages
                                  :conversation-id convo-id}}
               ::persist-messages! {:project-id project-id
                                    :conversation-id convo-id
                                    :messages [(message->server user-msg)]
                                    :on-success [::messages-persisted]}}
              {:db base-db
               ::create-conversation! {:project-id project-id
                                       :model model
                                       :on-success [::conversation-created-start-stream user-msg llm-messages]}})))
        (do
          (mixpanel/track "Assistant Budget Limit" {"status" "hard_limit"})
          {:db (-> db
                   (assoc-in [:project-assistant :streaming?] false)
                   (assoc-in [:project-assistant :error]
                             (get-translation "assistant.budget.hard-limit"
                                              "Monthly usage limit reached. Contact your account admin.")))})))))

(re-frame/reg-event-db
  ::clear-active-ptql
  (fn [db _]
    (update db :project-assistant dissoc :active-ptql)))

(re-frame/reg-event-fx
  ::stream-done
  (fn [{db :db} _]
    (let [db (-> db
                 (assoc-in [:project-assistant :streaming?] false)
                 (flush-pending-actions))
          all-msgs (get-in db [:project-assistant :messages])
          next-pos (get-in db [:project-assistant :next-position] 0)
          tail (->> all-msgs
                    (drop-while #(some? (:id %))) ;; drop already persisted
                    (remove #(= "user" (:role %))) ;; drop user messages (persistent on send)
                    (map-indexed (fn [i msg]
                                   (assoc msg :position (+ next-pos i))))
                    (vec))
          project-id (subs/get-current-project-id db)
          convo-id (get-in db [:project-assistant :conversation :id])
          db (update-in db [:project-assistant :messages]
                        (fn [msgs]
                          (-> (into [] (drop-last (count tail) msgs))
                              (into tail))))]
      (cond-> {:db (-> db (update-in [:project-assistant :next-position] (fnil + 0) (count tail)))}
        (and convo-id (seq tail))
        (assoc ::persist-messages! {:project-id project-id
                                    :conversation-id convo-id
                                    :messages (mapv message->server tail)
                                    :on-success [::messages-persisted]})))))

(re-frame/reg-event-db
  ::stream-error
  (fn [db [_ message]]
    (-> db
        (assoc-in [:project-assistant :streaming?] false)
        (assoc-in [:project-assistant :error] (or message "Error from assistant"))
        (flush-pending-actions))))

(re-frame/reg-event-fx
  ::new-conversation
  (fn [{db :db} _]
    (let [user-id (:id (subs/get-current-user db))
          project-id (subs/get-current-project-id db)]
      {:db (update db :project-assistant merge
                   {:messages []
                    :pending-actions-buffer []
                    :streaming? false
                    :error nil
                    :conversation nil
                    :next-position 0})
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id nil}})))

(re-frame/reg-event-fx
  ::conversation-created-start-stream
  (fn [{db :db} [_ user-msg llm-messages conversation]]
    (let [project-id (subs/get-current-project-id db)
          user-id (:id (subs/get-current-user db))
          conn-id (stream/conn-id @conn-state)
          url (get-in db [:options :project-assistant-url])]
      {:db (assoc-in db [:project-assistant :conversation] conversation)
       ::send! {:url url
                :conn-id conn-id
                :message {:type "chat"
                          :messages llm-messages
                          :conversation-id (:id conversation)}}
       ::persist-messages! {:project-id project-id
                            :conversation-id (:id conversation)
                            :messages [(message->server user-msg)]
                            :on-success [::messages-persisted]}
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id (:id conversation)}})))

(re-frame/reg-event-db
  ::messages-persisted
  (fn [db [_ persisted-msgs]]
    (update-in db [:project-assistant :messages]
               (fn [msgs]
                 (mapv (fn [msg]
                         (if-let [persisted (some #(when (= (:position %) (:position msg)) %) persisted-msgs)]
                           (assoc msg :id (:id persisted))
                           msg))
                       msgs)))))

(re-frame/reg-event-fx
  ::title-received
  (fn [{db :db} [_ {:keys [conversation-id title]}]]
    (let [current-id (get-in db [:project-assistant :conversation :id])
          current-title (get-in db [:project-assistant :conversation :title])]
      (if (and (= conversation-id current-id)
               (str/blank? current-title))
        {:db (assoc-in db [:project-assistant :conversation :title] title)
         ::patch-conversation-title! {:project-id (subs/get-current-project-id db)
                                      :conversation-id conversation-id
                                      :title title}}
        ;; NOTE: stale - user switched conversations or already renamed, ignore.
        {}))))

(re-frame/reg-event-db
  ::position-conflict
  (fn [db _]
    ;; TODO: refactor it later, right now just show simple banner
    (assoc-in db [:project-assistant :error]
              (get-translation "assistant.position-conflict"
                               "This conversation was modified in another tab. Refresh to continue."))))

(re-frame/reg-event-fx
  ::activate-ptql
  (fn [{db :db} [_ entity ptql]]
    (let [state-key (get subs/index-grid-keys entity)
          new-db (assoc-in db [:project-assistant :active-ptql] {:entity entity :query ptql})]
      (if (common-events/on-index? db entity)
        {:db new-db
         ::common-events/fire-select-filter! "_DEFAULT_"
         :dispatch [::common-events/fetch-entity-list entity state-key server/default-query]}
        {:db new-db
         :dispatch (common-events/push-to-index db entity)}))))

(re-frame/reg-event-fx
  ::activate-view
  (fn [{db :db} [_ entity view-id]]
    {:db (update db :project-assistant dissoc :active-ptql)
     :dispatch [::common-events/activate-view entity view-id]}))

(re-frame/reg-event-fx
  ::load-conversation
  (fn [{db :db} [_ conversation-id]]
    (let [project-id (subs/get-current-project-id db)]
      {::load-conversation! {:project-id project-id
                             :conversation-id conversation-id
                             :on-success [::conversation-loaded]
                             :on-not-found [::conversation-not-found]}})))

(re-frame/reg-event-fx
  ::conversation-loaded
  (fn [{db :db} [_ conversation]]
    (let [user-id (:id (subs/get-current-user db))
          project-id (subs/get-current-project-id db)
          messages (mapv server->message (:messages conversation))
          max-pos (or (some->> messages (seq) (map :position) (apply max)) -1)]
      {:db (update db :project-assistant merge
                   {:conversation (select-keys conversation [:id :title :model :created_at :updated_at])
                    :messages messages
                    :pending-actions-buffer []
                    :next-position (inc max-pos)
                    :streaming? false
                    :error nil})
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id (:id conversation)}})))

(re-frame/reg-event-fx
  ::conversation-not-found
  (fn [{db :db} _]
    (let [user-id (:id (subs/get-current-user db))
          project-id (subs/get-current-project-id db)]
      {:db (update db :project-assistant merge {:messages [] :conversation nil :next-position 0})
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id nil}})))

(re-frame/reg-event-fx
  ::switch-tab
  (fn [{db :db} [_ tab]]
    (let [project-id (subs/get-current-project-id db)]
      (cond-> {:db (assoc-in db [:project-assistant :active-tab] tab)}
        (= tab :history)
        (assoc ::fetch-conversations! {:project-id project-id
                                       :on-success [::conversations-loaded]})))))

(re-frame/reg-event-db
  ::conversations-loaded
  (fn [db [_ {:keys [data]}]]
    (assoc-in db [:project-assistant :conversations] (vec data))))

(re-frame/reg-event-db
  ::start-rename
  (fn [db [_ conversation-id current-title]]
    (assoc-in db [:project-assistant :rename-state]
              {:id conversation-id :draft (or current-title "")})))

(re-frame/reg-event-db
  ::update-rename-draft
  (fn [db [_ draft]]
    (assoc-in db [:project-assistant :rename-state :draft] draft)))

(re-frame/reg-event-db
  ::cancel-rename
  (fn [db _]
    (assoc-in db [:project-assistant :rename-state] nil)))

(re-frame/reg-event-fx
  ::commit-rename
  (fn [{db :db} _]
    (let [{:keys [id draft]} (get-in db [:project-assistant :rename-state])
          trimmed (str/trim (or draft ""))
          project-id (subs/get-current-project-id db)
          active-id (get-in db [:project-assistant :conversation :id])]
      (if (str/blank? trimmed)
        {:db (assoc-in db [:project-assistant :rename-state] nil)}
        (cond-> {:db (-> db
                         (assoc-in [:project-assistant :rename-state] nil)
                         (update-in [:project-assistant :conversations]
                                    (fn [cs]
                                      (mapv (fn [c]
                                              (if (= (:id c) id)
                                                (assoc c :title trimmed)
                                                c))
                                            cs))))
                 ::patch-conversation-title! {:project-id project-id
                                              :conversation-id id
                                              :title trimmed}}
          (= id active-id)
          (assoc-in [:db :project-assistant :conversation :title] trimmed))))))

(re-frame/reg-event-fx
  ::delete-conversation
  (fn [{db :db} [_ conversation-id]]
    (let [project-id (subs/get-current-project-id db)
          active-id (get-in db [:project-assistant :conversation :id])
          user-id (:id (subs/get-current-user db))
          conv (first (filter #(= (:id %) conversation-id)
                              (get-in db [:project-assistant :conversations])))]
      (cond-> {:db (update-in db [:project-assistant :conversations]
                              (fn [cs]
                                (vec (remove #(= (:id %) conversation-id) cs))))
               ::delete-conversation! {:project-id project-id
                                       :conversation-id conversation-id
                                       :on-success [::conversation-deleted conv]}}
        (= conversation-id active-id)
        (->
          ;; TODO: we're resetting active conversation in several places already, add a helper fn
          (update :db #(update % :project-assistant merge
                               {:messages []
                                :pending-actions-buffer []
                                :streaming? false
                                :error nil
                                :conversation nil
                                :next-position 0}))
          (assoc ::set-active-conversation! {:user-id user-id
                                             :project-id project-id
                                             :convo-id nil}))))))

(re-frame/reg-event-fx
  ::conversation-deleted
  (fn [_ [_ deleted-conv _deleted-id]]
    (let [toast-id (str "assistant-conv-delete-" (:id deleted-conv))]
      {::notifications/show2
       {:id toast-id
        :message (reagent/as-element
                   [:<>
                    (get-translation "assistant.history.deleted" "Conversation deleted.")
                    " "
                    [:a.assistant-undo-link
                     {:href "#"
                      :on-click (fn [e]
                                  (.preventDefault e)
                                  (notifications/dismiss toast-id)
                                  (re-frame/dispatch [::restore-conversation (:id deleted-conv)]))}
                     (get-translation "assistant.history.undo" "Undo")]])
        :type "info"
        :auto-close 5000}})))

(re-frame/reg-event-fx
  ::restore-conversation
  (fn [{db :db} [_ conversation-id]]
    (let [project-id (subs/get-current-project-id db)]
      {::restore-conversation! {:project-id project-id
                                :conversation-id conversation-id
                                :on-success [::conversation-restored]}})))

(re-frame/reg-event-db
  ::conversation-restored
  (fn [db [_ conv]]
    (update-in db [:project-assistant :conversations]
               (fn [cs]
                 (conj (vec (remove #(= (:id %) (:id conv)) (or cs []))) conv)))))

(re-frame/reg-event-fx
  ::reactivate-conversation
  (fn [{db :db} [_ conversation-id]]
    {:db (assoc-in db [:project-assistant :active-tab] :chat)
     :dispatch [::load-conversation conversation-id]}))

(re-frame/reg-event-db
  ::set-history-search
  (fn [db [_ query]]
    (assoc-in db [:project-assistant :history-search] query)))

;; --- views ---

(defn- assistant-tabs []
  (let [active @(re-frame/subscribe [::active-tab])]
    [tabs-container {:active-tab-id active
                     :on-toggle #(re-frame/dispatch [::switch-tab %])}
     [tab {:tab-id :chat :icon [icons/mux-message]}
      (get-translation "assistant.tab.chat" "Chat")]
     [tab {:tab-id :history :icon [icons/mux-clock]}
      (get-translation "assistant.tab.history" "History")]]))

(defn- conversation-row [conv]
  (let [rename-state @(re-frame/subscribe [::rename-state])
        editing? (= (:id rename-state) (:id conv))
        title (:title conv)
        display-title (if (str/blank? title)
                        (get-translation "assistant.new-chat" "New Chat")
                        title)]
    [:div.assistant-conversation-row
     (if editing?
       [:input.assistant-conversation-rename-input
        {:value (:draft rename-state)
         :auto-focus true
         :on-change #(re-frame/dispatch [::update-rename-draft (-> % .-target .-value)])
         :on-key-down (fn [e]
                        (case (.-key e)
                          "Enter" (re-frame/dispatch [::commit-rename])
                          "Escape" (re-frame/dispatch [::cancel-rename])
                          nil))
         :on-blur #(re-frame/dispatch [::commit-rename])}]
       [:<>
        [:div.assistant-conversation-row-main
         {:on-click #(re-frame/dispatch [::reactivate-conversation (:id conv)])}
         [:div.assistant-conversation-row-title display-title]
         (when-let [timestamp (:last_message_at conv)]
           [:div.assistant-conversation-row-time (fu/format-time-ago timestamp)])]
        [:div.assistant-conversation-row-actions
         [buttons/icon-button
          {:icon [icons/mux-pencil]
           :class "row-action"
           :on-click (fn [e]
                       (.stopPropagation e)
                       (re-frame/dispatch [::start-rename (:id conv) title]))}]
         [buttons/icon-button
          {:icon [icons/mux-trash]
           :class "row-action"
           :on-click (fn [e]
                       (.stopPropagation e)
                       (re-frame/dispatch [::delete-conversation (:id conv)]))}]]])]))

(defn- history-tab []
  (let [conversations @(re-frame/subscribe [::conversations])
        filtered @(re-frame/subscribe [::filtered-conversations])
        search @(re-frame/subscribe [::history-search])]
    [:div.assistant-history
     [:div.assistant-history-heading
      (get-translation "assistant.history.heading" "Chat History")]
     [search-box/search-textbox
      {:class "assistant-history-search"
       :value search
       :placeholder (get-translation "assistant.history.search" "Search")
       :on-change #(re-frame/dispatch [::set-history-search (-> % .-target .-value)])
       :on-clear-click #(re-frame/dispatch [::set-history-search ""])}]
     (cond
       (empty? conversations)
       [:div.assistant-empty-history
        (get-translation "assistant.history.empty" "No conversations yet")]

       (empty? filtered)
       [:div.assistant-empty-history
        (get-translation "assistant.history.no-results" "No results")]

       :else
       [:div.assistant-history-list
        (for [conv filtered]
          ^{:key (:id conv)}
          [conversation-row conv])])]))

(defn- message-bubble [{:keys [id role content] :as msg}]
  (case role
    "pending-action"
    [actions/pending-action-bubble msg]

    "tool-call"
    (when-not (str/blank? content)
      [:div.assistant-message.tool-call-message
       [:div.message-text [markdown-preview content]]])

    (when-not (str/blank? content)
      (if (= role "user")
        [:div.assistant-message-row.user-message-row
         [:div.user-message-avatar
          [navigation/user-avatar2 @(re-frame/subscribe [::subs/current-user])]]
         [:div.assistant-message.user-message
          [:div.message-text content]]]
        [:div.assistant-message.assistant-message-content
         [:div.assistant-message-label
          [:span.assistant-message-label-icon "✨"]
          [:span (get-translation "assistant.title" "Smart Fox Assistant")]]
         [:div.message-text [markdown-preview content {:link-target nil}]]
         (when id
           [feedback/feedback-component
            {:feature "assistant"
             :entity-type :assistant-message
             :entity-id id
             :class "assistant-feedback"
             :mixpanel-up ["Assistant Feedback" {"Rating" "Up"}]
             :mixpanel-down ["Assistant Feedback" {"Rating" "Down"}]}])]))))

(defn- welcome-screen []
  [:div.assistant-welcome
   [:div.welcome-title (get-translation "assistant.welcome.title" "How can I help?")]
   [:div.welcome-suggestions
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::send-message "Which tests haven't been run in the last 30 days?"])}
     (get-translation "assistant.welcome.stale-tests" "Tests not run in the last 30 days")]
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::send-message "Show requirements without linked tests"])}
     (get-translation "assistant.welcome.unlinked-reqs" "Requirements without linked tests")]
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::send-message "Show issues assigned to me"])}
     (get-translation "assistant.welcome.my-issues" "Issues assigned to me")]
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::send-message "Show tests linked to open issues"])}
     (get-translation "assistant.welcome.tests-with-issues" "Tests linked to open issues")]]])

;; NOTE: the scroll-to-bottom thing is a little hairy here
;;       not sure it worth the effort, maybe we should just always show "scroll-to-bottom" button?
(defn- messages-list []
  (let [container-ref (atom nil)
        bottom-ref (atom nil)
        programmatic-scroll? (atom false)
        at-bottom? (reagent/atom true)]
    (fn []
      (let [messages @(re-frame/subscribe [::messages])
            streaming? @(re-frame/subscribe [::streaming?])
            error @(re-frame/subscribe [::error])
            at-bottom-p? (fn [el]
                           (< (- (.-scrollHeight el) (.-scrollTop el) (.-clientHeight el)) 50))]
        (when-let [el @container-ref]
          (let [is-at-bottom? (at-bottom-p? el)]
            (when (not= @at-bottom? is-at-bottom?)
              (reset! at-bottom? is-at-bottom?))))
        (when (and @at-bottom? @bottom-ref)
          (reset! programmatic-scroll? true)
          (js/requestAnimationFrame
            (fn []
              (.scrollIntoView @bottom-ref #js {:behavior "smooth"})
              (js/setTimeout #(reset! programmatic-scroll? false) 100))))
        [:<>
         (when (and (empty? messages) (not streaming?))
           [welcome-screen])
         [:div.assistant-messages
          {:ref (fn [el]
                  (reset! container-ref el)
                  (when el
                    (reset! at-bottom? (at-bottom-p? el))))
           :on-scroll (fn []
                        (when-not @programmatic-scroll?
                          (when-let [el @container-ref]
                            (reset! at-bottom? (at-bottom-p? el)))))}
          (for [[idx msg] (map-indexed vector messages)]
            ^{:key idx}
            [message-bubble msg])
          (when error
            [:div.assistant-error error])
          (when-not @at-bottom?
            [:div.assistant-scroll-to-bottom
             {:on-click (fn []
                          (reset! at-bottom? true)
                          (when @bottom-ref
                            (.scrollIntoView @bottom-ref #js {:behavior "smooth"})))}
             [icons/mux-arrow-down]])
          [:div {:ref #(reset! bottom-ref %)
                 :style {:height "1em"}}]]]))))

(defn- input-box []
  (let [input-value (reagent/atom "")
        input-ref (atom nil)]
    (fn []
      (let [streaming? @(re-frame/subscribe [::streaming?])
            connected? @(re-frame/subscribe [::connected?])
            send! (fn []
                    (when (and (not (str/blank? @input-value))
                               (not streaming?)
                               connected?)
                      (re-frame/dispatch [::send-message @input-value])
                      (reset! input-value "")
                      (when @input-ref
                        (set! (.-height (.-style @input-ref)) "auto")
                        (.focus @input-ref))))]
        [:<>
         (when streaming?
           [:div.assistant-processing-indicator
            [:span (get-translation "assistant.processing" "Processing Result")]
            [spinners/mux-spinner {:size :small}]])
         [:div.assistant-input
          [:textarea.m-input
           {:ref (fn [el]
                   (reset! input-ref el)
                   (when el
                     (dom/focus-on-ref! input-ref false)))
            :value @input-value
            :placeholder (get-translation "assistant.input.placeholder" "Message Smart Fox Assistant...")
            :disabled (not connected?)
            :rows 1
            :on-change (fn [e]
                         (reset! input-value (dom/event-value e))
                         ;; TODO: try `field-sizing: content` (might not work in FF)
                         (let [el (.-target e)]
                           (set! (.-height (.-style el)) "auto")
                           (set! (.-height (.-style el)) (str (.-scrollHeight el) "px"))))
            :on-key-down (fn [e]
                           (when (and (= (.-key e) "Enter")
                                      (not (.-shiftKey e)))
                             (.preventDefault e)
                             (send!)))}]
          [buttons/button
           {:primary? true
            :disabled? (or streaming? (not connected?) (str/blank? @input-value))
            :icon (if streaming?
                    [icons/mux-square]
                    [icons/mux-arrow-right])
            :on-click send!}]]]))))

(defn- panel-header []
  (let [title @(re-frame/subscribe [::conversation-title])]
    [:div.assistant-panel-header
     [:div.assistant-title-area
      [:div.assistant-title-row
       [:span.assistant-title-icon "✨"]
       [:span.assistant-title (get-translation "assistant.title" "Smart Fox Assistant")]
       [:span.assistant-beta-badge (get-translation "assistant.beta" "Beta")]]
      [:div.assistant-conversation-title
       (if (str/blank? title)
         (get-translation "assistant.new-chat" "New Chat")
         title)]]
     [:div.assistant-header-actions
      [buttons/button
       {:class "assistant-new-button"
        :icon [icons/mux-plus-circle]
        :text (get-translation "assistant.new-conversation" "New")
        :on-click #(re-frame/dispatch [::new-conversation])}]
      [buttons/icon-button
       {:icon [icons/x-circle]
        :on-click #(re-frame/dispatch [::close-panel])}]]]))

(defn assistant-panel []
  (let [budget-warning? @(re-frame/subscribe [::budget-warning?])
        active-tab @(re-frame/subscribe [::active-tab])
        streaming? @(re-frame/subscribe [::streaming?])]
    [:div.assistant-panel
     {:class (when streaming? "streaming")}
     [panel-header]
     [assistant-tabs]
     (if (= active-tab :history)
       [history-tab]
       [:<>
        [messages-list]
        (when budget-warning?
          [:div.assistant-budget-warning
           (get-translation "assistant.budget.warning" "Approaching monthly usage limit")])
        [input-box]])]))

(defn assistant-available? []
  (and @(re-frame/subscribe [::subs/generative-ai-consent?])
       @(re-frame/subscribe [::subs/project-assistant-enabled?])))

(comment
  (re-frame/dispatch [::dev-add-pending-action
                      "create_custom_view"
                      {:entity_type "Issue"
                       :name "My critical bugs"
                       :view_ptql "Priority = \"High\""
                       :parent_id 21621
                       :parent_path "Issues / Open"}]))
